import React, { useRef, useState } from 'react';

/**
 * A plain <textarea> with Slack/GitHub-style "@" mention autocomplete
 * bolted on. Typing "@" followed by any letters opens a dropdown of
 * matching employees; picking one (click, Enter, or Tab) inserts their
 * name into the text and adds them to the tagged-people list, which is
 * what actually drives the notification + "you were tagged" marker on
 * the backend -- the "@Name" in the text itself is just visual.
 *
 * Deliberately keeps its own textarea rather than wrapping a rich-text
 * editor: no extra dependency, and the only thing that needs to be
 * "smart" here is detecting the word being typed after the last "@".
 *
 * Props:
 *   - value, onChange(newText): the comment body itself
 *   - employees: full employee directory [{ mavis_user_id, display_name, username }]
 *   - taggedIds, onTaggedIdsChange(newIds): who's currently tagged
 *   - placeholder, disabled, minHeight
 *   - onBlur: optional, fires after the mention popover's own blur handling
 *     (e.g. for a "save on blur" field like the ticket description)
 */
const MentionTextarea = ({
  value, onChange, employees, taggedIds, onTaggedIdsChange,
  placeholder, disabled, minHeight = 60, id, onBlur,
}) => {
  const textareaRef = useRef(null);
  const [mentionQuery, setMentionQuery] = useState(null); // null = popover closed
  const [mentionStart, setMentionStart] = useState(null); // index of the triggering '@'
  const [highlightIndex, setHighlightIndex] = useState(0);

  const matches = mentionQuery === null
    ? []
    : employees
      .filter((e) => (e.display_name || e.username || '').toLowerCase().includes(mentionQuery.toLowerCase()))
      .slice(0, 6);

  // Looks at the text up to the caret; if it ends in "@word" (word being
  // typed right now, no space yet), that's an active mention to complete.
  const detectMention = (text, caretPos) => {
    const upToCaret = text.slice(0, caretPos);
    const match = upToCaret.match(/(?:^|\s)@(\w*)$/);
    if (match) {
      setMentionStart(upToCaret.lastIndexOf('@'));
      setMentionQuery(match[1]);
      setHighlightIndex(0);
    } else {
      setMentionStart(null);
      setMentionQuery(null);
    }
  };

  const closeMention = () => {
    setMentionStart(null);
    setMentionQuery(null);
  };

  const handleChange = (e) => {
    onChange(e.target.value);
    detectMention(e.target.value, e.target.selectionStart);
  };

  const handleClickOrKeyUp = (e) => {
    detectMention(e.target.value, e.target.selectionStart);
  };

  const selectEmployee = (emp) => {
    const name = (emp.display_name || emp.username || '').replace(/\s+/g, '');
    const before = value.slice(0, mentionStart);
    const after = value.slice(mentionStart + 1 + mentionQuery.length);
    const insertion = `@${name} `;
    const newText = `${before}${insertion}${after}`;
    onChange(newText);
    if (!taggedIds.includes(emp.mavis_user_id)) {
      onTaggedIdsChange([...taggedIds, emp.mavis_user_id]);
    }
    closeMention();
    requestAnimationFrame(() => {
      if (textareaRef.current) {
        const pos = before.length + insertion.length;
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(pos, pos);
      }
    });
  };

  const handleKeyDown = (e) => {
    if (mentionQuery === null || matches.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex((i) => Math.min(i + 1, matches.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      selectEmployee(matches[highlightIndex]);
    } else if (e.key === 'Escape') {
      closeMention();
    }
  };

  const removeTag = (empId) => {
    onTaggedIdsChange(taggedIds.filter((t) => t !== empId));
  };

  const taggedEmployees = employees.filter((e) => taggedIds.includes(e.mavis_user_id));

  return (
    <div style={{ position: 'relative' }}>
      <textarea
        id={id}
        ref={textareaRef}
        className="form-textarea"
        style={{ minHeight }}
        placeholder={placeholder}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onKeyUp={handleClickOrKeyUp}
        onClick={handleClickOrKeyUp}
        onBlur={() => {
          setTimeout(closeMention, 150);
          if (onBlur) onBlur();
        }}
        disabled={disabled}
      />
      {mentionQuery !== null && matches.length > 0 && (
        <div className="date-picker-popover" role="listbox" aria-label="Tag someone" style={{ width: 220, padding: '0.3rem' }}>
          {matches.map((emp, i) => (
            <div
              key={emp.mavis_user_id}
              role="option"
              aria-selected={i === highlightIndex}
              onMouseDown={(e) => { e.preventDefault(); selectEmployee(emp); }}
              onMouseEnter={() => setHighlightIndex(i)}
              style={{
                padding: '0.4rem 0.5rem',
                borderRadius: 'var(--radius-sm)',
                cursor: 'pointer',
                fontSize: '0.85rem',
                background: i === highlightIndex ? 'var(--accent-soft-bg)' : 'transparent',
                color: i === highlightIndex ? 'var(--accent)' : 'inherit',
              }}
            >
              {emp.display_name || emp.username}
            </div>
          ))}
        </div>
      )}
      {taggedEmployees.length > 0 && (
        <div style={{ marginTop: '0.35rem', display: 'flex', gap: '0.3rem', flexWrap: 'wrap' }}>
          {taggedEmployees.map((emp) => (
            <span key={emp.mavis_user_id} className="label-chip">
              @{emp.display_name || emp.username}
              <button
                type="button"
                onClick={() => removeTag(emp.mavis_user_id)}
                aria-label={`Remove tag ${emp.display_name || emp.username}`}
                style={{ marginLeft: '0.3rem', background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', padding: 0 }}
              >
                &times;
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default MentionTextarea;
